/**
 * Wait before each automatic retry of a failed read. The length is the retry
 * cap: once every entry is used the failure reaches the caller.
 */
export const FAILED_READ_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

/** Arguments for {@link retryFailedRead}. */
interface RetryFailedReadArgs<T> {
  /** Runs one attempt of the read. */
  read: () => Promise<T>;
  /**
   * Wait before each retry; its length is the number of retries after the
   * first attempt. When omitted, `FAILED_READ_RETRY_DELAYS_MS` is used.
   */
  delaysMs?: readonly number[];
  /**
   * Aborting it ends a pending wait and stops further attempts. When omitted,
   * the retries always run to the cap.
   */
  signal?: AbortSignal;
}

/**
 * Resolves after `delayMs`, or sooner when the browser comes back online, the
 * tab becomes visible, or `signal` aborts.
 */
function waitForRetry({
  delayMs,
  signal,
}: {
  delayMs: number;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise(resolve => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const finish = () => {
      clearTimeout(timer);
      window.removeEventListener('online', finish);
      document.removeEventListener('visibilitychange', finishWhenVisible);
      signal?.removeEventListener('abort', finish);
      resolve();
    };
    const finishWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        finish();
      }
    };
    const timer = setTimeout(finish, delayMs);
    window.addEventListener('online', finish);
    document.addEventListener('visibilitychange', finishWhenVisible);
    signal?.addEventListener('abort', finish);
  });
}

/**
 * Runs a one-shot read, retrying when it rejects. A read with no listener
 * never recovers from a dropped connection unless it is asked for again.
 *
 * @param args - See {@link RetryFailedReadArgs}.
 * @return What the first successful attempt resolved to.
 * @throws The last attempt's rejection once the retries are used up, or the
 *     latest rejection as soon as `signal` aborts.
 */
export async function retryFailedRead<T>({
  read,
  delaysMs = FAILED_READ_RETRY_DELAYS_MS,
  signal,
}: RetryFailedReadArgs<T>): Promise<T> {
  for (const delayMs of delaysMs) {
    try {
      return await read();
    } catch (error) {
      console.debug('[retryFailedRead] read failed, will retry', error);
      await waitForRetry({delayMs, signal});
      if (signal?.aborted) {
        throw error;
      }
    }
  }
  return read();
}
